// ./src/utils/locationHelpers.ts
// Type-only: `@/hooks` pulls in useGoogleMapsAutocomplete, which imports from
// `@/utils` — `import type` is erased at compile time so the cycle never exists
// at runtime.
import type { AddressData, PlaceData } from "@/hooks";

/**
 * Format address data into a short display string (for place auto-fill)
 */
export const formatAddressShort = (address: AddressData): string => {
  const parts: string[] = [];

  if (address.address1) {
    parts.push(address.address1);
  }
  if (address.city) {
    parts.push(address.city);
  }
  if (address.region) {
    parts.push(address.region);
  }

  return parts.join(", ");
};

/**
 * Format address data into a full display string (for location field)
 * Includes: address1, city, region, postal code, country
 */
export const formatAddressFull = (address: AddressData): string => {
  const parts: string[] = [];

  // Street address
  if (address.address1) {
    parts.push(address.address1);
  }

  // City
  if (address.city) {
    parts.push(address.city);
  }

  // Region/State with postal code (e.g., "AB T2P 1J9" or "CA 90210")
  const regionPostal: string[] = [];
  if (address.region) {
    regionPostal.push(address.region);
  }
  if (address.postalCode) {
    regionPostal.push(address.postalCode);
  }
  if (regionPostal.length > 0) {
    parts.push(regionPostal.join(" "));
  }

  // Country
  if (address.country) {
    parts.push(address.country);
  }

  return parts.join(", ");
};

/**
 * One line that identifies a picked place COMPLETELY — for anywhere we store a
 * chosen location as a single string instead of structured columns.
 *
 * Two rules, and both matter:
 *
 * 1. Never store `displayName` alone. Google returns it as the business name for
 *    an establishment ("Tim Hortons") and as the street line alone for a plain
 *    address — either way the city, region and country are lost, which leaves a
 *    driver with an address they can't find.
 * 2. Only PREFIX the name when the pick was a named place. For a plain address
 *    `displayName` is just the street line again, and prefixing it produced
 *    "160 Woodglen Way SW, 160 Woodglen Way Southwest, Calgary…". Don't try to
 *    detect that by comparing strings — Google abbreviates the two differently
 *    ("SW" vs "Southwest"), so any substring test misses. `isEstablishment`
 *    comes from Google's own place types and is the authoritative signal;
 *    absent (a geocoded or saved address) means "not a named place".
 */
export const formatPlaceLine = (address: AddressData): string => {
  const line = formatAddressFull(address);
  const name = (address.displayName ?? "").trim();

  // No structured components to fall back on (typed free text like "Grandma's
  // house", or a geocode that resolved nothing) — the name is all we have.
  if (!line) {
    return name;
  }

  if (!name || !address.isEstablishment) {
    return line;
  }

  // A named place whose name Google already folded into the address text.
  if (line.toLowerCase().includes(name.toLowerCase())) {
    return line;
  }

  return `${name}, ${line}`;
};

/**
 * `formatPlaceLine` for a **place search** result rather than an address search.
 *
 * Same output, same rules — the only difference is where the name lives.
 * `PlaceData` carries it as `place.name` (from Google's `displayName`), while the
 * nested `place.address` carries the structured components. Everything else,
 * including the establishment-vs-address decision, is delegated so the two entry
 * points can never drift.
 *
 * Prefer this over reaching into `place.address` yourself: a caller that formats
 * `place.address` alone silently drops the place name.
 */
export const formatPlaceLineFromPlace = (place: PlaceData): string => {
  const address = place.address;

  if (!address) {
    return (place.name || "").trim();
  }

  return formatPlaceLine({
    ...address,
    displayName: (place.name || "").trim() || address.displayName,
  });
};
// =============================================================================
// Distance (accommodation distance-from-venue)
// =============================================================================

/**
 * Great-circle distance between two lat/lng points, in metres. Returns null when
 * either coordinate is missing, so callers can leave the distance blank rather than
 * showing a bogus "0 m".
 */
export const haversineMeters = (
  lat1?: number | null,
  lng1?: number | null,
  lat2?: number | null,
  lng2?: number | null
): number | null => {
  if (
    typeof lat1 !== "number" ||
    typeof lng1 !== "number" ||
    typeof lat2 !== "number" ||
    typeof lng2 !== "number"
  ) {
    return null;
  }

  const R = 6371000; // Earth radius, metres
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);

  return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
};

/**
 * A short "distance from venue" label. Under 1 km shows metres; otherwise km with one
 * decimal. Returns null for a missing distance so the caller renders a dash.
 */
export const formatDistanceFromMeters = (meters?: number | null): string | null => {
  if (meters === null || meters === undefined || Number.isNaN(meters)) {
    return null;
  }

  if (meters < 1000) {
    return `${Math.round(meters)} m`;
  }

  return `${(meters / 1000).toFixed(1)} km`;
};
